// React port of Angular product-preview.component.ts. Domain-aware
// attribute-driven preview with Sketch / Photo / AI / Layout tabs.
// Reads the picked product + style + preferences from the shared
// productSelectionStore, or from page-vars when configured.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRuntime } from '@/pages/workbench/smartpage-preview/RuntimeContext'
import type { ProductPreviewBlock } from '@/pages/designers/page/types'
import {
  generateAiImage, getStyleComposite, getStylePreview, previewAiImagePayload,
  type ErpAiImageReference, type ErpAiImageSkippedRef,
  type ErpCompositeResult, type ErpPreviewSpec,
} from '../erpService'
import { readBinding } from '../bindingResolver'
import { useProductSelectionState } from '../productSelectionState'
import { injectEcomStyles } from './styles'

interface PreviewProductLike {
  id: string
  name?: string
  imageUrl?: string
  basePrice?: number
  currency?: string
}
interface PreviewStyleLike {
  id: string
  name?: string
}

type Tab = 'sketch' | 'photo' | 'ai' | 'layout'

function initials(name?: string): string {
  if (!name) return '?'
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || '?'
}

function matchesWhen(when: Record<string, string | string[]> | undefined, prefs: Record<string, unknown>): boolean {
  if (!when) return true
  for (const [k, expected] of Object.entries(when)) {
    const actual = prefs[k]
    if (Array.isArray(expected)) {
      if (!expected.includes(String(actual))) return false
    } else if (String(actual) !== expected) {
      return false
    }
  }
  return true
}

export default function ProductPreviewRenderer({
  block, variant = 'garment',
}: {
  block: ProductPreviewBlock
  /** When embedded inside product_selection, pick which preview shape
   *  to render. Standalone usage falls through to 'garment'. */
  variant?: 'garment' | 'gallery'
}) {
  injectEcomStyles()
  const runtime = useRuntime()
  const sel = useProductSelectionState()

  const useStateSource = (block.attributesSource || 'product_selection_state') === 'product_selection_state'
  const ctx = useMemo(
    () => ({ vars: runtime.vars, inputs: runtime.page.inputs || [] }),
    [runtime.vars, runtime.page.inputs],
  )

  // ── Resolve source values ──────────────────────────────
  const product: PreviewProductLike | null = useMemo(() => {
    if (useStateSource) return sel.selectedProduct
    const v = readBinding(block.productBinding, ctx)
    return v && typeof v === 'object' ? (v as PreviewProductLike) : null
  }, [useStateSource, sel.selectedProduct, block.productBinding, ctx])

  const style: PreviewStyleLike | null = useMemo(() => {
    if (useStateSource) return sel.selectedStyle
    const v = readBinding(block.styleBinding, ctx)
    return v && typeof v === 'object' ? (v as PreviewStyleLike) : null
  }, [useStateSource, sel.selectedStyle, block.styleBinding, ctx])

  const prefValues: Record<string, unknown> = useMemo(() => {
    if (useStateSource) return sel.prefValues
    const v = readBinding(block.attributesBinding, ctx)
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : {}
  }, [useStateSource, sel.prefValues, block.attributesBinding, ctx])

  const measValues: Record<string, number | ''> = useStateSource ? sel.measValues : {}
  const prefSchema = sel.prefSchema

  const [tab, setTab] = useState<Tab>('photo')
  const [spec, setSpec] = useState<ErpPreviewSpec | null>(null)
  const [loadingSpec, setLoadingSpec] = useState(false)
  const [composite, setComposite] = useState<ErpCompositeResult | null>(null)
  const [loadingComposite, setLoadingComposite] = useState(false)
  const [photoErrored, setPhotoErrored] = useState(false)

  const [aiImage, setAiImage] = useState<string | null>(null)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiRefs, setAiRefs] = useState<ErpAiImageReference[]>([])
  const [aiSkipped, setAiSkipped] = useState<ErpAiImageSkippedRef[]>([])
  const [aiSource, setAiSource] = useState('')
  const [aiHasGenerated, setAiHasGenerated] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [showDetails, setShowDetails] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [aiError, setAiError] = useState('')

  const compositeDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previewDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastCompositeKey = useRef<string | null>(null)
  const lastPreviewKey = useRef<string | null>(null)
  const lastStyleId = useRef<string | null>(null)

  // ── Spec fetch when style changes ──────────────────────
  useEffect(() => {
    const id = style?.id ?? null
    if (id === lastStyleId.current) return
    lastStyleId.current = id
    if (!id) { setSpec(null); return }
    let cancelled = false
    setLoadingSpec(true)
    getStylePreview(id).then(
      (s) => { if (!cancelled && lastStyleId.current === id) { setSpec(s); setLoadingSpec(false) } },
      () => { if (!cancelled && lastStyleId.current === id) { setSpec(null); setLoadingSpec(false) } },
    )
    return () => { cancelled = true }
  }, [style?.id])

  // ── Composite refresh on pref change ────────────────────
  useEffect(() => {
    if (compositeDebounce.current) clearTimeout(compositeDebounce.current)
    compositeDebounce.current = setTimeout(() => { refreshComposite() }, 200)
    return () => { if (compositeDebounce.current) clearTimeout(compositeDebounce.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec, style?.id, JSON.stringify(prefValues)])

  function refreshComposite() {
    const styleId = style?.id
    if (!spec?.compositeImageEndpoint || !styleId) { setComposite(null); return }
    const key = styleId + '|' + Object.entries(prefValues)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}=${v}`)
      .join('&')
    if (key === lastCompositeKey.current) return
    lastCompositeKey.current = key
    setLoadingComposite(true)
    setPhotoErrored(false)
    getStyleComposite(styleId, prefValues, spec.compositeImageEndpoint).then(
      (res) => {
        if (lastCompositeKey.current !== key) return
        setComposite(res); setLoadingComposite(false)
      },
      () => { if (lastCompositeKey.current === key) setLoadingComposite(false) },
    )
  }

  // ── AI preview payload refresh ──────────────────────────
  useEffect(() => {
    if (tab !== 'ai') return
    if (previewDebounce.current) clearTimeout(previewDebounce.current)
    previewDebounce.current = setTimeout(() => { refreshPreviewPayload() }, 250)
    return () => { if (previewDebounce.current) clearTimeout(previewDebounce.current) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, style?.id, JSON.stringify(prefValues)])

  function refreshPreviewPayload() {
    const styleId = style?.id
    if (!styleId) return
    const key = `${styleId}|${JSON.stringify(prefValues)}`
    if (key === lastPreviewKey.current && aiHasGenerated) return
    lastPreviewKey.current = key
    setPreviewLoading(true)
    previewAiImagePayload(styleId, prefValues).then(
      (res) => {
        // Don't clobber a real generation result.
        if (aiHasGenerated && key === lastPreviewKey.current) {
          setPreviewLoading(false)
          return
        }
        setAiPrompt(res.prompt || '')
        setAiRefs(res.referenceImages || [])
        setAiSkipped(res.skippedReferences || [])
        setPreviewLoading(false)
      },
      () => setPreviewLoading(false),
    )
  }

  // ── Stage images ────────────────────────────────────────
  const sketchImage = composite?.fallbackImageUrl || spec?.baseImageUrl || product?.imageUrl || null
  const photoImage = composite
    ? (photoErrored && composite.fallbackImageUrl ? composite.fallbackImageUrl : composite.imageUrl)
    : (spec?.baseImageUrl || product?.imageUrl || null)

  function aspectStyle(): string {
    const a = spec?.aspect || block.aspect || '1:1'
    if (a === '4:3') return '4 / 3'
    if (a === '3:4') return '3 / 4'
    if (a === '16:9') return '16 / 9'
    return '1 / 1'
  }

  // ── Feature gallery / chips ─────────────────────────────
  const featureCards = useMemo(() => {
    const fields = prefSchema?.fields || []
    const annotations = spec?.annotations && spec.annotations.length > 0
      ? spec.annotations
      : fields.map((f) => f.key)
    const cards: Array<{ key: string; fieldLabel: string; label: string; imageUrl?: string; swatch?: string }> = []
    const seen = new Set<string>()
    for (const key of annotations) {
      const value = prefValues[key]
      if (value === undefined || value === null || value === '') continue
      const f = fields.find((x) => x.key === key)
      const opt = f?.options?.find((o) => o.value === value)
      const cardKey = `${key}=${value}`
      seen.add(cardKey)
      cards.push({
        key: cardKey,
        fieldLabel: f?.label || key,
        label: opt?.label || String(value),
        imageUrl: opt?.imageUrl,
        swatch: opt?.swatch,
      })
    }
    for (const layer of spec?.layers || []) {
      if (!matchesWhen(layer.when, prefValues)) continue
      const cardKey = 'layer:' + Object.entries(layer.when).map(([k, v]) => `${k}=${v}`).join('+')
      if (seen.has(cardKey)) continue
      const overlapped = Object.entries(layer.when).some(([k, v]) => seen.has(`${k}=${v}`))
      if (overlapped) continue
      seen.add(cardKey)
      cards.push({
        key: cardKey,
        fieldLabel: 'Detail',
        label: layer.label || '',
        imageUrl: layer.imageUrl ?? undefined,
      })
    }
    return cards
  }, [spec, prefValues, prefSchema])

  const measurementCount = Object.values(measValues).filter((v) => v !== undefined && v !== '').length

  // ── Caption ──
  const caption = useMemo(() => {
    const tmpl = spec?.captionTemplate || '{{styleName}}'
    const fields = prefSchema?.fields || []
    const tokens: Record<string, string> = {
      productName: product?.name || '',
      styleName: style?.name || '',
    }
    for (const f of fields) {
      const v = prefValues[f.key]
      if (v === undefined || v === null || v === '') continue
      const opt = f.options?.find((o) => o.value === v)
      tokens[`${f.key}.value`] = String(v)
      tokens[`${f.key}.label`] = opt?.label || String(v)
    }
    return tmpl
      .replace(/\{\{\s*([\w.\-]+)\s*\}\}/g, (_m, key: string) => tokens[key] ?? '')
      .replace(/\s+·\s+(?=·)/g, ' · ')
      .replace(/(?:\s+·\s+)+$/g, '')
      .replace(/^(?:\s+·\s+)+/g, '')
      .trim()
  }, [spec, prefSchema, prefValues, product?.name, style?.name])

  function generate() {
    const styleId = style?.id
    if (!styleId || generating) return
    setGenerating(true)
    setAiError('')
    setTab('ai')
    generateAiImage(styleId, prefValues).then(
      (res) => {
        setAiImage(res.imageUrl)
        setAiPrompt(res.prompt || '')
        setAiRefs(res.referenceImages || [])
        setAiSkipped(res.skippedReferences || [])
        setAiSource(res.source || '')
        setAiHasGenerated(true)
        setGenerating(false)
      },
      (err) => {
        const msg = (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message
        setAiError(msg || 'AI generation failed. Try again.')
        setGenerating(false)
      },
    )
  }

  function clearAi() {
    setAiImage(null); setAiPrompt(''); setAiRefs([]); setAiSkipped([])
    setAiSource(''); setAiHasGenerated(false); setAiError('')
    lastPreviewKey.current = null
    if (tab === 'ai') refreshPreviewPayload()
  }

  // ── Gallery variant — short-circuit to a thumbnail strip ──
  if (variant === 'gallery') {
    return <GalleryVariant product={product} style={style} />
  }

  // ── Empty state ──
  if (!product) {
    return (
      <section className={`pp${block.layout === 'large' ? ' pp--large' : block.layout === 'inline' ? ' pp--inline' : ''}`}>
        <div className="pp__empty">
          <div className="pp__empty-icon">🪞</div>
          <div className="pp__empty-title">{block.emptyStateTitle || 'Live preview'}</div>
          {block.emptyStateBody && <div className="pp__empty-body">{block.emptyStateBody}</div>}
        </div>
      </section>
    )
  }

  return (
    <section className={`pp${block.layout === 'large' ? ' pp--large' : block.layout === 'inline' ? ' pp--inline' : ''}`}>
      <div className="pp__tabs" role="tablist">
        <button type="button" role="tab"
                className={`pp__tab${tab === 'sketch' ? ' is-active' : ''}`}
                aria-selected={tab === 'sketch'}
                onClick={() => setTab('sketch')}>
          <span aria-hidden="true">✎</span><span>Sketch</span>
        </button>
        <button type="button" role="tab"
                className={`pp__tab${tab === 'photo' ? ' is-active' : ''}`}
                aria-selected={tab === 'photo'}
                onClick={() => setTab('photo')}>
          <span aria-hidden="true">📷</span><span>Photo</span>
        </button>
        <button type="button" role="tab"
                className={`pp__tab${tab === 'ai' ? ' is-active' : ''}`}
                aria-selected={tab === 'ai'}
                onClick={() => setTab('ai')}>
          <span aria-hidden="true">✦</span><span>AI</span>
          {aiImage && <span className="pp__tab-dot" />}
        </button>
        <button type="button" role="tab"
                className={`pp__tab${tab === 'layout' ? ' is-active' : ''}`}
                aria-selected={tab === 'layout'}
                onClick={() => setTab('layout')}>
          <span aria-hidden="true">✋</span><span>Layout</span>
        </button>
      </div>

      {tab === 'ai' && (
        <div className="pp__aitop">
          <div className="pp__aitop-row">
            {aiImage ? (
              <>
                <button type="button" className="pp__btn pp__btn--primary"
                        onClick={generate} disabled={generating}>
                  ✦ {generating ? 'Regenerating…' : 'Regenerate'}
                </button>
                <button type="button" className="pp__btn pp__btn--ghost"
                        onClick={clearAi} disabled={generating}>Clear</button>
              </>
            ) : (
              <button type="button" className="pp__btn pp__btn--primary"
                      onClick={generate} disabled={generating || !style}>
                ✦ {generating ? 'Generating…' : 'Generate with AI'}
              </button>
            )}
            <button type="button" className="pp__btn pp__btn--link"
                    onClick={() => setShowDetails((v) => !v)}
                    aria-expanded={showDetails}>
              {showDetails ? 'Hide' : 'View'} {aiHasGenerated ? 'prompt details' : 'what will be sent'}
            </button>
          </div>
          {aiError && <div className="pp__aierr pp__aierr--top">{aiError}</div>}
          {showDetails && (
            <div className="pp__details">
              <div className={`pp__details-banner${aiHasGenerated ? ' pp__details-banner--live' : ''}`}>
                {aiHasGenerated ? (
                  <span>✓ Sent to <strong>{aiSource || 'AI model'}</strong> · this is the exact prompt and {aiRefs.length} reference image(s) the model received</span>
                ) : previewLoading ? (
                  <span>Loading what will be sent…</span>
                ) : (
                  <span>Preview · this is what will be sent to the AI when you click Generate</span>
                )}
              </div>
              {aiPrompt && (
                <div className="pp__details-row pp__details-row--col">
                  <span className="pp__details-key">Prompt</span>
                  <pre className="pp__details-prompt">{aiPrompt}</pre>
                </div>
              )}
              {aiRefs.length > 0 ? (
                <div className="pp__details-row pp__details-row--col">
                  <span className="pp__details-key">
                    Reference images passed to the model ({aiRefs.length})
                  </span>
                  <div className="pp__refgrid">
                    {aiRefs.map((r, i) => (
                      <figure key={r.imageUrl} className="pp__refcard" title={r.label}>
                        {r.imageUrl ? (
                          <img className="pp__refthumb" src={r.imageUrl} alt={r.label} />
                        ) : (
                          <span className="pp__refthumb pp__refthumb--blank">?</span>
                        )}
                        <figcaption className="pp__refcap">
                          <span className="pp__refidx">{i + 1}</span>
                          <span className="pp__reflabel">{r.label}</span>
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              ) : !previewLoading ? (
                <div className="pp__details-row pp__details-row--col">
                  <span className="pp__details-key">Reference images</span>
                  <span className="pp__details-empty">
                    No reference images yet — pick a fabric, button, pocket or cuff style to attach component photos to the prompt.
                  </span>
                </div>
              ) : null}
              {aiSkipped.length > 0 && (
                <div className="pp__details-row pp__details-row--col">
                  <span className="pp__details-key">
                    Skipped ({aiSkipped.length}) · the model will not see these
                  </span>
                  <div className="pp__refgrid pp__refgrid--skipped">
                    {aiSkipped.map((r) => (
                      <figure key={r.imageUrl} className="pp__refcard pp__refcard--skipped" title={r.reason}>
                        <img className="pp__refthumb" src={r.imageUrl} alt={r.label} />
                        <figcaption className="pp__refcap">
                          <span className="pp__reflabel">{r.label}</span>
                          <span className="pp__refreason">{r.reason}</span>
                        </figcaption>
                      </figure>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'layout' ? (
        <div className="pp__layouthint">
          Placement editor is available in the production runtime. Live preview shows the picked style without inline-anchor editing.
        </div>
      ) : (
        <div className={`pp__stage${tab !== 'sketch' ? ' pp__stage--photo' : ''}`}
             style={{ aspectRatio: aspectStyle() }}>
          {tab === 'sketch' && (
            sketchImage ? (
              <img className="pp__base pp__base--contain" src={sketchImage}
                   alt={`${product.name || 'Product'} technical sketch`} />
            ) : (
              <div className="pp__base pp__base--placeholder">{initials(product.name)}</div>
            )
          )}
          {tab === 'sketch' && (
            <div className="pp__livebadge pp__livebadge--sketch">
              <span className="pp__livedot" />
              <span>Sketch · live</span>
            </div>
          )}

          {tab === 'photo' && (
            <>
              {photoImage ? (
                <img className="pp__base pp__base--cover"
                     src={photoImage}
                     alt={product.name || 'Product'}
                     onError={() => setPhotoErrored(true)} />
              ) : (
                <div className="pp__base pp__base--placeholder">{initials(product.name)}</div>
              )}
              {composite?.tintColor && (
                <div className="pp__tint"
                     style={{
                       background: composite.tintColor,
                       opacity: composite.tintOpacity ?? 0.4,
                       mixBlendMode: (composite.tintBlendMode || 'multiply') as React.CSSProperties['mixBlendMode'],
                     }} />
              )}
              <div className="pp__livebadge">
                <span className="pp__livedot" />
                <span>Live preview</span>
              </div>
              {composite?.featureBadges && composite.featureBadges.length > 0 && (
                <div className="pp__corner">
                  {composite.featureBadges.map((b) => (
                    <div key={b.key} className="pp__cornerchip">
                      {b.imageUrl ? (
                        <span className="pp__cornerthumb"
                              style={{ backgroundImage: `url(${b.imageUrl})` }} />
                      ) : b.swatch ? (
                        <span className="pp__cornerthumb pp__cornerthumb--swatch"
                              style={{ background: b.swatch }} />
                      ) : null}
                      <span className="pp__cornermeta">
                        <span className="pp__cornerfield">{b.fieldLabel}</span>
                        <span className="pp__cornerlabel">{b.label}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === 'ai' && (
            aiImage ? (
              <>
                <img className="pp__base pp__base--contain" src={aiImage} alt="AI rendering" />
                <div className="pp__aibadge">
                  <span aria-hidden="true">✦</span><span>AI generated</span>
                </div>
              </>
            ) : (
              <div className="pp__aiprompt">
                <div className="pp__aipromptIcon">✦</div>
                <div className="pp__aipromptTitle">Generate a photoreal render</div>
                <div className="pp__aipromptBody">
                  Click the button below to send your selections — colour, fabric, collar, cuff, pocket, measurements — to the AI image generator.
                </div>
              </div>
            )
          )}

          {(loadingSpec || loadingComposite || generating) && (
            <div className="pp__loading">{generating ? 'Generating with AI…' : 'Rendering preview…'}</div>
          )}
        </div>
      )}

      {tab === 'ai' && aiImage && composite?.description && (
        <p className="pp__aidesc">{composite.description}</p>
      )}

      {block.showAttributeChips !== false && featureCards.length > 0 && (
        <>
          <div className="pp__featurelabel">Customisations</div>
          <div className="pp__featuregallery">
            {featureCards.map((card) => (
              <figure key={card.key} className="pp__featurecard">
                {card.imageUrl ? (
                  <span className="pp__featurethumb"
                        style={{ backgroundImage: `url(${card.imageUrl})` }} />
                ) : card.swatch ? (
                  <span className="pp__featurethumb pp__featurethumb--swatch"
                        style={{ background: card.swatch }} />
                ) : (
                  <span className="pp__featurethumb pp__featurethumb--blank">{initials(card.label)}</span>
                )}
                <figcaption className="pp__featurecap">
                  <span className="pp__featurefield">{card.fieldLabel}</span>
                  <span className="pp__featurevalue">{card.label}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </>
      )}

      {measurementCount > 0 && (
        <div className="pp__measbadge">
          <span className="pp__measicon" aria-hidden="true">📏</span>
          <span>{measurementCount} measurements captured</span>
        </div>
      )}

      {block.showCaption !== false && caption && !aiImage && (
        <p className="pp__caption">{caption}</p>
      )}
    </section>
  )
}

// ── Gallery variant ──────────────────────────────────────────
// Lightweight non-tailored e-commerce gallery: hero image + small
// thumbnail strip. Sourced from the picked product/style imageUrl.
function GalleryVariant({
  product, style,
}: { product: PreviewProductLike | null; style: PreviewStyleLike | null }) {
  if (!product) {
    return (
      <section className="pp pp--inline">
        <div className="pp__empty">
          <div className="pp__empty-icon">🪞</div>
          <div className="pp__empty-title">Live preview</div>
        </div>
      </section>
    )
  }
  const hero = product.imageUrl || ''
  return (
    <section className="pp pp--inline">
      <div className="pp__stage pp__stage--photo" style={{ aspectRatio: '1 / 1' }}>
        {hero ? (
          <img className="pp__base pp__base--cover" src={hero} alt={product.name || 'Product'} />
        ) : (
          <div className="pp__base pp__base--placeholder">{initials(product.name)}</div>
        )}
        <div className="pp__livebadge">
          <span className="pp__livedot" />
          <span>Live preview</span>
        </div>
      </div>
      {style?.name && <p className="pp__caption">{style.name}</p>}
    </section>
  )
}
